import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { Tooltip } from "@omni/types/components/general/toolip.model";
import { ECommerceTooltips } from "@omni/types/components/product/eCommerce/eCommerce.type";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import * as SessionStorage from "@omni/utils/sessionStorage.utils";
import { createContext, use } from "react";

import { validateTooltip } from "@/components/Product/ECommerce/eCommerce.helpers";
import { DeliveryOption } from "@/components/Product/ECommerce/eCommerce.types";
import { AdditionalServiceModel } from "@/types/service/shared";
import { StorageKeys } from "@/utils/enums";

type DeliveryItemContextValue = {
    sku: string;
    option: DeliveryOption;
    deliveryTypesDisabled: boolean;
    checked: boolean;
    onChangeDeliveryType: () => void;
    montageParents: string[];
    deliveryTooltipItem?: Tooltip;
    serviceProductTooltipItem?: Tooltip;
    montages: Array<AdditionalServiceModel>;
    activeServiceProducts?: Array<string>;
    onChangeServiceProduct: (serviceProduct: AdditionalServiceModel) => void;
    isLimitedStock: boolean;
    infoLink: string;
    isBoxArticle: boolean;
    boxLogisticInformation?: string | null;
    preferredStore: CloudStore | null;
    minimumOrderQuantity: number;
    xfPath?: string | null;
    roundingValue: number;
};

export type DeliveryItemContextProviderProps = {
    sku: string;
    option: DeliveryOption;
    deliveryTypesDisabled: boolean;
    activeDelivery: DeliveryType;
    changeDelivery: (deliveryType: DeliveryType) => void;
    montageParents: string[];
    tooltips: ECommerceTooltips;
    montages: Array<AdditionalServiceModel>;
    activeServiceProducts?: Array<string>;
    changeServiceProduct: (
        serviceProduct: AdditionalServiceModel,
        deliveryType: DeliveryType
    ) => void;
    isLimitedStock: boolean;
    infoLink: string;
    isBoxArticle: boolean;
    boxLogisticInformation?: string | null;
    preferredStore: CloudStore | null;
    minimumOrderQuantity: number;
    xfPath?: string | null;
    roundingValue: number;
};

export const DeliveryItemContext = createContext<
    DeliveryItemContextValue | undefined
>(undefined);

DeliveryItemContext.displayName = "DeliveryItemContext";

export const DeliveryItemContextProvider = ({
    sku,
    option,
    activeDelivery,
    changeDelivery,
    deliveryTypesDisabled,
    activeServiceProducts,
    montageParents,
    tooltips,
    montages,
    children,
    changeServiceProduct,
    isLimitedStock,
    infoLink,
    isBoxArticle,
    boxLogisticInformation,
    preferredStore,
    minimumOrderQuantity,
    xfPath,
    roundingValue
}: RequireChildrenFor<DeliveryItemContextProviderProps>) => {
    const onChangeDeliveryType = () => {
        SessionStorage.setItem(StorageKeys.DeliveryType, option.deliveryType);
        changeDelivery(option.deliveryType);
    };

    const onChangeServiceProduct = (serviceProduct: AdditionalServiceModel) => {
        changeServiceProduct(serviceProduct, option.deliveryType);
    };

    const checked = option.deliveryType === activeDelivery;

    const deliveryTooltipItem = validateTooltip(tooltips[option.deliveryType]);
    const serviceProductTooltipItem = validateTooltip(tooltips.serviceProduct);

    return (
        <DeliveryItemContext.Provider
            value={{
                sku,
                option,
                deliveryTypesDisabled,
                deliveryTooltipItem,
                serviceProductTooltipItem,
                checked,
                montageParents,
                montages,
                activeServiceProducts,
                onChangeDeliveryType,
                onChangeServiceProduct,
                isLimitedStock,
                infoLink,
                isBoxArticle,
                preferredStore,
                boxLogisticInformation,
                minimumOrderQuantity,
                xfPath,
                roundingValue
            }}
        >
            {children}
        </DeliveryItemContext.Provider>
    );
};

export const useDeliveryItemContext = (): DeliveryItemContextValue => {
    const context = use<DeliveryItemContextValue | undefined>(
        DeliveryItemContext
    );
    if (!isDefined(context)) {
        throw new Error(`${DeliveryItemContext.displayName} is undefined.`);
    }
    return context;
};
